import { ComposerSnapshot, ProfileChooser, serviceLabel } from "./protocol";

export interface ComposerIssue {
  id: string;
  service: string | null;
  description: string;
}

export interface ComposerEventInput {
  addedChannels: string[];
  removedChannels: string[];
  issues: ComposerIssue[];
}

interface CharLimitError {
  characterCount: number;
  characterLimit: number;
  service: string;
}

interface TagLimitError {
  tagCount: number;
  tagLimit: number;
}

export const deriveComposerIssues = (snapshot: ComposerSnapshot): ComposerIssue[] => {
  const { charLimit, tagLimit } = snapshot.validationErrors as { charLimit?: CharLimitError; tagLimit?: TagLimitError };
  const captionIssues: ComposerIssue[] = [];
  if (charLimit) {
    captionIssues.push({
      id: `charLimit:${charLimit.service}`,
      service: charLimit.service,
      description: `The caption is ${charLimit.characterCount} characters; the ${serviceLabel(charLimit.service)} limit is ${charLimit.characterLimit}.`
    });
  }
  if (tagLimit) {
    captionIssues.push({
      id: "tagLimit",
      service: null,
      description: `The caption has ${tagLimit.tagCount} hashtags; the limit is ${tagLimit.tagLimit}.`
    });
  }
  const attachmentIssues = snapshot.attachments.issues.map(issue => ({
    id: `attachment:${issue.service ?? "all"}:${issue.message}`,
    service: issue.service,
    description: issue.message
  }));
  return [...captionIssues, ...attachmentIssues];
};

const formatIssues = (issues: ComposerIssue[]): string | null => {
  if (issues.length === 0) return null;
  if (issues.length === 1) return `New problem: ${issues[0].description}`;
  return `New problems:\n${issues.map(issue => `- ${issue.description}`).join("\n")}`;
};

export const formatComposerEvent = ({ addedChannels, removedChannels, issues }: ComposerEventInput): string =>
  [
    addedChannels.length ? `Channels added: ${addedChannels.join(", ")}.` : null,
    removedChannels.length ? `Channels removed: ${removedChannels.join(", ")}.` : null,
    formatIssues(issues)
  ]
    .filter(Boolean)
    .join("\n");

export interface ChannelChangeIds {
  added: string[];
  removed: string[];
}

export const diffChannels = (before: string[], after: string[]): ChannelChangeIds => ({
  added: after.filter(id => !before.includes(id)),
  removed: before.filter(id => !after.includes(id))
});

export interface ProfileCheckInput {
  profileCount: number;
  profileChosenBy: ProfileChooser | null;
  hasCaption: boolean;
  checkedChannelIds: string[] | null; // the selection the assistant last saw; null before the chat has a baseline
  selectedChannelIds: string[];
}

// Only a caption written for one profile can end up on another profile's channels; an empty post is checked when the assistant writes.
export const shouldCheckProfile = ({ profileCount, profileChosenBy, hasCaption, checkedChannelIds, selectedChannelIds }: ProfileCheckInput): boolean => {
  const mayAutoSwitch = profileChosenBy === null || profileChosenBy === "assistant";
  if (profileCount < 2 || !mayAutoSwitch || !hasCaption || checkedChannelIds === null || selectedChannelIds.length === 0) return false;
  const { added, removed } = diffChannels(checkedChannelIds, selectedChannelIds);
  return added.length > 0 || removed.length > 0;
};
